import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import { clearAuthStorage, getMustChangePassword, getStoredRole, getStoredToken, persistAuthSession } from '../utils/auth';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
    const location = useLocation();
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getStoredToken()));

    const token = getStoredToken();
    const userRole = getStoredRole();
    const mustChangePassword = getMustChangePassword();

    const redirectTarget = useMemo(() => ({
        pathname: '/login',
        state: { from: location.pathname }
    }), [location.pathname]);

    useEffect(() => {
        let isMounted = true;

        const validateSession = async () => {
            if (!getStoredToken()) {
                if (isMounted) {
                    setIsAuthenticated(false);
                    setIsChecking(false);
                }
                return;
            }

            try {
                const response = await authAPI.validateSession();

                if (!isMounted) {
                    return;
                }

                persistAuthSession({
                    token: getStoredToken(),
                    user: response.data?.data?.user
                });
                setIsAuthenticated(true);
            } catch (error) {
                if (!isMounted) {
                    return;
                }

                clearAuthStorage();
                setIsAuthenticated(false);
            } finally {
                if (isMounted) {
                    setIsChecking(false);
                }
            }
        };

        const handlePageShow = () => {
            validateSession();
        };

        validateSession();
        window.addEventListener('pageshow', handlePageShow);

        return () => {
            isMounted = false;
            window.removeEventListener('pageshow', handlePageShow);
        };
    }, [location.pathname]);

    if (isChecking) {
        // No token at all — redirect immediately, no need to wait for API call
        if (!token) {
            return <Navigate to={redirectTarget} replace />;
        }
        if (mustChangePassword && location.pathname !== '/change-password') {
            return <Navigate to="/change-password" replace />;
        }
        if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
            return <Navigate to="/unauthorized" replace />;
        }
        return children;
    }

    if (!token || !isAuthenticated) {
        return <Navigate to={redirectTarget} replace />;
    }

    if (mustChangePassword && location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
        return <Navigate to="/unauthorized" replace />;
    }

    return children;
};

export default ProtectedRoute;
